/** Sets Department. Prop inventory + blueprint media + milestone bars. */
import { useState } from "react";
import { Plus, Trash2, Box, Flag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import useCubicleRecords from "./useCubicleRecords";

const PROP_STATUSES = ["needed", "sourced", "onset", "wrapped"];

export default function SetsCubicle({ projectId }) {
  const props      = useCubicleRecords(projectId, "sets", "prop");
  const milestones = useCubicleRecords(projectId, "sets", "milestone");
  const [pDlg, setPDlg] = useState(false);
  const [mDlg, setMDlg] = useState(false);
  const [pForm, setPForm] = useState({ name: "", category: "", qty: 1,
                                        status: "needed", image_url: "" });
  const [mForm, setMForm] = useState({ name: "", target_date: "", progress: 0 });

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="sets-cubicle">
      {/* Prop inventory */}
      <section className="cs-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Prop Inventory
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {props.items.length} prop(s)
            </div>
          </div>
          <Button onClick={() => setPDlg(true)}
                  className="h-8 gap-1 bg-white text-black hover:bg-white/90"
                  data-testid="prop-add">
            <Plus size={13}/> Prop
          </Button>
        </div>
        {props.items.length === 0 ? (
          <div className="text-sm text-white/45 py-6 text-center">
            No props tracked yet.
          </div>
        ) : (
          <div className="space-y-2" data-testid="prop-list">
            {props.items.map((r) => {
              const d = r.data || {};
              const status = d.status || "needed";
              return (
                <div key={r.id}
                     className="rounded-lg border border-white/10 bg-black/30 p-3 flex items-center gap-3"
                     data-testid={`prop-row-${r.id}`}>
                  <div className="w-10 h-10 rounded-md bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
                    {d.image_url ? (
                      <img src={d.image_url} alt="" className="w-full h-full object-cover"
                           onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                    ) : <Box size={16} className="text-white/45"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{d.name || "Prop"}</div>
                    <div className="text-[11px] text-white/45">
                      {d.category || "misc"} · qty {d.qty ?? 1}
                    </div>
                  </div>
                  <select value={status}
                          onChange={(e) => props.patch(r.id, { status: e.target.value })}
                          className="h-8 rounded-md bg-black/40 border border-white/15 px-2 text-[11px] text-white/85"
                          data-testid={`prop-status-${r.id}`}>
                    {PROP_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <Button variant="ghost" onClick={() => props.remove(r.id)}
                          className="h-7 px-2 text-white/45 hover:text-rose-300"
                          data-testid={`prop-remove-${r.id}`}>
                    <Trash2 size={13}/>
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Milestones */}
      <section className="cs-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">
              Build Milestones
            </div>
            <div className="font-display text-lg uppercase tracking-tight">
              {milestones.items.length} tracked
            </div>
          </div>
          <Button onClick={() => setMDlg(true)}
                  className="h-8 gap-1 bg-white text-black hover:bg-white/90"
                  data-testid="milestone-add">
            <Plus size={13}/> Milestone
          </Button>
        </div>
        {milestones.items.length === 0 ? (
          <div className="text-sm text-white/45 py-6 text-center">
            Track set-build milestones with progress bars.
          </div>
        ) : (
          <div className="space-y-3" data-testid="milestone-list">
            {milestones.items.map((r) => {
              const d = r.data || {};
              const pct = Math.max(0, Math.min(100, Number(d.progress) || 0));
              return (
                <div key={r.id}
                     className="rounded-lg border border-white/10 bg-black/30 p-3"
                     data-testid={`milestone-row-${r.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Flag size={13} className="text-white/60"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{d.name || "Milestone"}</div>
                      {d.target_date && (
                        <div className="text-[11px] text-white/45">target {d.target_date}</div>
                      )}
                    </div>
                    <span className="text-[11px] text-white/70 tabular-nums w-11 text-right">{pct}%</span>
                    <Button variant="ghost" onClick={() => milestones.remove(r.id)}
                            className="h-7 px-2 text-white/45 hover:text-rose-300"
                            data-testid={`milestone-remove-${r.id}`}>
                      <Trash2 size={13}/>
                    </Button>
                  </div>
                  <div className="relative h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-emerald-400 rounded-full"
                         style={{ width: `${pct}%` }}/>
                  </div>
                  <input type="range" min={0} max={100} step={5}
                         value={pct}
                         onChange={(e) => milestones.patch(r.id, { progress: Number(e.target.value) })}
                         className="w-full mt-2 accent-emerald-400"
                         data-testid={`milestone-slider-${r.id}`}/>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Prop dialog */}
      <Dialog open={pDlg} onOpenChange={setPDlg}>
        <DialogContent className="max-w-md bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">Add Prop</DialogTitle>
            <DialogDescription className="sr-only">Prop inventory row.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label>
              <Input value={pForm.name}
                     onChange={(e) => setPForm({ ...pForm, name: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="prop-name-input"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Category</Label>
                <Input value={pForm.category}
                       onChange={(e) => setPForm({ ...pForm, category: e.target.value })}
                       className="mt-1 bg-black/40 border-white/15"/></div>
              <div><Label>Qty</Label>
                <Input type="number" min={1} value={pForm.qty}
                       onChange={(e) => setPForm({ ...pForm, qty: Number(e.target.value) })}
                       className="mt-1 bg-black/40 border-white/15"/></div>
            </div>
            <div><Label>Image URL</Label>
              <Input value={pForm.image_url}
                     onChange={(e) => setPForm({ ...pForm, image_url: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPDlg(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (!pForm.name.trim()) return;
              await props.create("prop", pForm);
              setPDlg(false);
              setPForm({ name: "", category: "", qty: 1, status: "needed", image_url: "" });
            }} className="bg-white text-black hover:bg-white/90"
               data-testid="prop-submit">Save prop</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Milestone dialog */}
      <Dialog open={mDlg} onOpenChange={setMDlg}>
        <DialogContent className="max-w-md bg-neutral-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display text-xl uppercase tracking-tight">Add Milestone</DialogTitle>
            <DialogDescription className="sr-only">Build milestone bar.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label>
              <Input value={mForm.name}
                     onChange={(e) => setMForm({ ...mForm, name: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"
                     data-testid="milestone-name-input"/></div>
            <div><Label>Target date</Label>
              <Input type="date" value={mForm.target_date}
                     onChange={(e) => setMForm({ ...mForm, target_date: e.target.value })}
                     className="mt-1 bg-black/40 border-white/15"/></div>
            <div><Label>Initial progress %</Label>
              <Input type="number" min={0} max={100} value={mForm.progress}
                     onChange={(e) => setMForm({ ...mForm, progress: Number(e.target.value) })}
                     className="mt-1 bg-black/40 border-white/15"/></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setMDlg(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (!mForm.name.trim()) return;
              await milestones.create("milestone", mForm);
              setMDlg(false);
              setMForm({ name: "", target_date: "", progress: 0 });
            }} className="bg-white text-black hover:bg-white/90"
               data-testid="milestone-submit">Save milestone</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
